import { ReportResponseDtoStatus } from '@/api/WhisApi/generated';
import { useReportsFindVersions } from '@/api/WhisApi/generated/reports/reports';
import { KeyValueRow } from '@/components/KeyValueRow/KeyValueRow';
import { StatusBadge } from '@/components/StatusBadge/StatusBadge';
import { showToast } from '@/components/Toast/toast.service';
import { useAppTranslation } from '@/hooks/useAppTranslation';
import { Ionicons } from '@expo/vector-icons';
import { useRouter } from 'expo-router';
import { useEffect, useState } from 'react';
import { Pressable } from 'react-native';
import { H4, Separator, SizableText, XStack, YStack } from 'tamagui';
import { ReportVersionsSheet } from './ReportVersion/ReportVersionsSheet';
import { formatVersionNumber } from './ReportVersion/reportDetailVersions.utils';

export interface ReportDetailHeaderProps {
  code: string;
  status: ReportResponseDtoStatus;
  author: string;
  creationDate: string | undefined;
  country: string;
  reportId: string;
  versionNumber: number;
}

export const ReportDetailHeader = ({
  code,
  status,
  author,
  creationDate,
  country,
  reportId,
  versionNumber,
}: ReportDetailHeaderProps): React.ReactElement => {
  const { t } = useAppTranslation();
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const router = useRouter();
  const {
    data: versionsData,
    error,
    isFetching,
  } = useReportsFindVersions(reportId, {
    query: { enabled: isSheetOpen },
  });

  const formattedVersionNumber = formatVersionNumber(versionNumber);

  useEffect(() => {
    if (!isSheetOpen || !error) {
      return;
    }
    console.error('Failed to load report versions', error);
    showToast({
      type: 'error',
      title: t('reportDetail.toasts.loadVersionsError'),
    });
  }, [error, isSheetOpen, t]);

  const versionItems = versionsData?.reports ?? [];

  const handleSelectVersion = (versionId: string): void => {
    if (versionId === reportId) {
      setIsSheetOpen(false);
      return;
    }
    router.setParams({ reportId: versionId });
    setIsSheetOpen(false);
  };

  return (
    <YStack gap="$true">
      <XStack justifyContent="space-between" alignItems="center" gap="$2">
        <H4>{code}</H4>
        <StatusBadge status={status} />
      </XStack>
      <Separator borderColor="$color" />
      <YStack gap="$2">
        <KeyValueRow label={t('reportDetail.header.author')} value={author} />
        <KeyValueRow
          label={t('reportDetail.header.creationDate')}
          value={creationDate}
        />
        <KeyValueRow label={t('reportDetail.header.country')} value={country} />
        <Pressable
          onPress={() => setIsSheetOpen(true)}
          disabled={versionNumber === 0}
        >
          <XStack
            gap="$true"
            justifyContent="space-between"
            alignItems="center"
          >
            <SizableText color="$secondary75" flex={1} size="$2">
              {t('reportDetail.header.version')}
            </SizableText>
            <XStack alignItems="center" gap="$1">
              <SizableText color="$secondary" size="$2">
                {formattedVersionNumber}
              </SizableText>
              {versionNumber !== 0 && (
                <Ionicons name="chevron-down" size={12} />
              )}
            </XStack>
          </XStack>
        </Pressable>
      </YStack>
      <ReportVersionsSheet
        open={isSheetOpen}
        onOpenChange={setIsSheetOpen}
        versions={versionItems}
        selectedVersionId={reportId}
        isLoading={isFetching}
        onSelectVersion={(version) => handleSelectVersion(version.id)}
      />
    </YStack>
  );
};
